import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { settingsAPI } from '../services/api';

const SETTINGS_CACHE_KEY = 'kadolar_system_settings_v1';

const DEFAULT_SYSTEM_SETTINGS = {
  site_name: 'کادولار',
  logo_url: null,
  favicon_url: null,
  pwa_icon_url: null,
  pwa_short_name: 'کادولار',
  pwa_title: '',
  pwa_window_color: '#dc2626',
  pwa_window_text_color: '#ffffff',
  header_subtitle: 'فروشگاه هدایای لوکس',
  terms_and_conditions: '',
  privacy_policy: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  contact_instagram_url: '',
  contact_telegram_url: '',
  contact_whatsapp_url: '',
  contact_rubika_url: '',
  contact_eitaa_url: '',
  contact_map_embed_url: '',
  read_only_mode: false,
  read_only_message: '',
  feature_flags: {},
  header_collapse_enabled: false,
  header_scroll_threshold: 140,
  header_scroll_hysteresis: 120,
  loop_bar_enabled: true,
  loop_bar_texts: [],
  loop_bar_direction: 'rtl',
  loop_bar_speed_seconds: 126,
  loop_bar_delay_ms: 1000,
  loop_bar_height_px: 50,
  loop_bar_item_gap_px: 32,
  loop_bar_end_gap_px: 200,
  loop_bar_bg_css: 'linear-gradient(to right, #7a0000, #c10000, #7a0000)',
  loop_bar_text_color: '#ffffff',
  loop_bar_font_family: '',
  loop_bar_max_width_px: null,
  loop_bar_show_on_home_mobile: false,
  banner_auto_change_ms: 3000,
  banners: [],
  about_gallery_media: [],
};

const mapDataToSystemSettings = (data) => ({
  site_name: data.site_name || 'کادولار',
  logo_url: data.logo_url || null,
  favicon_url: data.favicon_url || null,
  pwa_icon_url: data.pwa_icon_url || null,
  pwa_short_name: String(data.pwa_short_name || 'کادولار').slice(0, 12) || 'کادولار',
  pwa_title: String(data.pwa_title || '').slice(0, 100) || '',
  pwa_window_color: String(data.pwa_window_color || '#dc2626').slice(0, 9) || '#dc2626',
  pwa_window_text_color: String(data.pwa_window_text_color || '#ffffff').slice(0, 9) || '#ffffff',
  header_subtitle: String(data.header_subtitle || 'فروشگاه هدایای لوکس').slice(0, 100) || 'فروشگاه هدایای لوکس',
  terms_and_conditions: data.terms_and_conditions || '',
  privacy_policy: data.privacy_policy || '',
  contact_email: data.contact_email || '',
  contact_phone: data.contact_phone || '',
  address: data.address || '',
  contact_instagram_url: data.contact_instagram_url || '',
  contact_telegram_url: data.contact_telegram_url || '',
  contact_whatsapp_url: data.contact_whatsapp_url || '',
  contact_rubika_url: data.contact_rubika_url || '',
  contact_eitaa_url: data.contact_eitaa_url || '',
  contact_map_embed_url: data.contact_map_embed_url || '',
  read_only_mode: !!data.read_only_mode,
  read_only_message: data.read_only_message || '',
  feature_flags: data.feature_flags || {},
  header_collapse_enabled: data.header_collapse_enabled !== undefined ? !!data.header_collapse_enabled : false,
  header_scroll_threshold: Number.isFinite(Number(data.header_scroll_threshold)) ? Number(data.header_scroll_threshold) : 140,
  header_scroll_hysteresis: Number.isFinite(Number(data.header_scroll_hysteresis)) ? Number(data.header_scroll_hysteresis) : 120,
  loop_bar_enabled: data.loop_bar_enabled !== undefined ? !!data.loop_bar_enabled : true,
  loop_bar_texts: Array.isArray(data.loop_bar_texts) ? data.loop_bar_texts : [],
  loop_bar_direction: String(data.loop_bar_direction || '').toLowerCase() === 'ltr' ? 'ltr' : 'rtl',
  loop_bar_speed_seconds: Number.isFinite(Number(data.loop_bar_speed_seconds)) ? Number(data.loop_bar_speed_seconds) : 126,
  loop_bar_delay_ms: Number.isFinite(Number(data.loop_bar_delay_ms)) ? Number(data.loop_bar_delay_ms) : 1000,
  loop_bar_height_px: Number.isFinite(Number(data.loop_bar_height_px)) ? Number(data.loop_bar_height_px) : 50,
  loop_bar_item_gap_px: Number.isFinite(Number(data.loop_bar_item_gap_px)) ? Math.max(0, Number(data.loop_bar_item_gap_px)) : 32,
  loop_bar_end_gap_px: Number.isFinite(Number(data.loop_bar_end_gap_px)) ? Math.max(0, Number(data.loop_bar_end_gap_px)) : 200,
  loop_bar_bg_css: data.loop_bar_bg_css || 'linear-gradient(to right, #7a0000, #c10000, #7a0000)',
  loop_bar_text_color: data.loop_bar_text_color || '#ffffff',
  loop_bar_font_family: data.loop_bar_font_family || '',
  loop_bar_max_width_px: data.loop_bar_max_width_px === null || data.loop_bar_max_width_px === undefined
    ? null
    : (Number.isFinite(Number(data.loop_bar_max_width_px)) ? Number(data.loop_bar_max_width_px) : null),
  loop_bar_show_on_home_mobile: data.loop_bar_show_on_home_mobile !== undefined ? !!data.loop_bar_show_on_home_mobile : false,
  banner_auto_change_ms: Number.isFinite(Number(data.banner_auto_change_ms)) ? Number(data.banner_auto_change_ms) : 3000,
  banners: Array.isArray(data.banners) ? data.banners : [],
  about_gallery_media: Array.isArray(data.about_gallery_media) ? data.about_gallery_media : [],
});

const SettingsContext = createContext({
  systemSettings: DEFAULT_SYSTEM_SETTINGS,
  resolveMediaUrl: (u) => u,
  reloadSystemSettings: async () => {},
});

export const SettingsProvider = ({ children }) => {
  const [systemSettings, setSystemSettings] = useState(() => {
    try {
      const raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
      if (!raw) return DEFAULT_SYSTEM_SETTINGS;
      const cached = JSON.parse(raw);
      if (!cached || typeof cached !== 'object') return DEFAULT_SYSTEM_SETTINGS;
      return { ...DEFAULT_SYSTEM_SETTINGS, ...cached };
    } catch (e) {
      return DEFAULT_SYSTEM_SETTINGS;
    }
  });

  const API_BASE = (() => {
    // خالی → relative (تک‌origin). فقط مقدار صریح آدرس بدهیم.
    const rawEnv = (process.env.REACT_APP_API_URL ?? '').trim();
    const raw = rawEnv || (typeof window === 'undefined' ? 'http://localhost:8000/api' : '/api');
    if (typeof window === 'undefined') return raw;
    try {
      const u = new URL(raw, window.location.href);
      const isLocalBackend = u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '0.0.0.0';
      const h = window.location.hostname || '';
      const isLocalFrontend = h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
      if (process.env.NODE_ENV !== 'production' && isLocalBackend && !isLocalFrontend && h) {
        const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
        return `${protocol}//${h}:8000/api`;
      }
    } catch (e) {}
    return raw;
  })();
  const SETTINGS_ENABLED = (() => {
    const raw = (process.env.REACT_APP_SETTINGS_ENABLED || '').trim();
    if (raw) return raw === 'true';
    return true;
  })();
  const MEDIA_ORIGIN = useMemo(() => API_BASE.replace(/\/$/, '').replace(/\/api$/, ''), [API_BASE]);

  const resolveMediaUrl = useCallback((u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    // Remove duplicate slashes if any, ensure single slash between origin and path
    const path = u.startsWith('/') ? u : `/${u}`;
    return `${MEDIA_ORIGIN}${path}`;
  }, [MEDIA_ORIGIN]);

  const reloadSystemSettings = useCallback(async () => {
    if (!SETTINGS_ENABLED) return;
    try {
      const resp = await settingsAPI.getSystem();
      const data = resp?.data || {};
      if (process.env.REACT_APP_DEBUG_CONSOLE === 'true') console.log('SettingsContext: Raw API Response:', data); // DEBUG
      const mapped = mapDataToSystemSettings(data);
      if (process.env.REACT_APP_DEBUG_CONSOLE === 'true') console.log('SettingsContext: Mapped Settings:', mapped); // DEBUG
      setSystemSettings(mapped);
      try {
        window.localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(mapped));
      } catch (e) {}
    } catch (e) {
      console.error('SettingsContext: Error fetching settings:', e); // DEBUG
    }
  }, [SETTINGS_ENABLED]);

  useEffect(() => {
    if (!SETTINGS_ENABLED) return; // Skip fetching when backend endpoint is absent
    let mounted = true;
    const dbg = process.env.REACT_APP_DEBUG_CONSOLE === 'true';
    const load = async () => {
      try {
        const resp = await settingsAPI.getSystem();
        const data = resp?.data || {};
        if (!mounted) return;
        if (dbg) console.log('SettingsContext: Raw API Response (Initial Load):', data);
        const mapped = mapDataToSystemSettings(data);
        if (dbg) console.log('SettingsContext: Mapped Settings (Initial Load):', mapped);
        setSystemSettings(mapped);
        try {
          window.localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(mapped));
        } catch (e) {}
      } catch (e) {
        if (dbg) console.error('SettingsContext: Error fetching settings (Initial Load):', e);
      }
    };
    load();
    return () => { mounted = false; };
  }, [SETTINGS_ENABLED]);

  useEffect(() => {
    const urls = Array.isArray(systemSettings?.banners)
      ? systemSettings.banners
          .map((b) => resolveMediaUrl(b?.image_url))
          .filter(Boolean)
          .slice(0, 6)
      : [];
    urls.forEach((src) => {
      const img = new window.Image();
      img.decoding = 'async';
      img.src = src;
    });
  }, [systemSettings?.banners, resolveMediaUrl]);

  // ── PWA / favicon / apple-touch-icon sync ──
  // When admin uploads a new logo/favicon/pwa_icon via Django admin,
  // update all icon links so the installed PWA icon matches the site logo.
  useEffect(() => {
    const pwaIconHref = resolveMediaUrl(systemSettings.pwa_icon_url);
    const faviconHref = resolveMediaUrl(systemSettings.favicon_url);
    const logoHref = resolveMediaUrl(systemSettings.logo_url);

    // 1) Update favicon / shortcut icon
    if (faviconHref || pwaIconHref) {
      const href = faviconHref || pwaIconHref;
      const ensureLink = (rel) => {
        let link = document.querySelector(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      };
      ensureLink('icon');
      ensureLink('shortcut icon');
    }

    // 2) Update apple-touch-icon (PWA install icon on iOS).
    //    Priority: pwa_icon (dedicated) > logo > favicon.
    const appleHref = pwaIconHref || logoHref || faviconHref;
    if (appleHref) {
      let appleLink = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleLink) {
        appleLink = document.createElement('link');
        appleLink.setAttribute('rel', 'apple-touch-icon');
        document.head.appendChild(appleLink);
      }
      appleLink.setAttribute('href', appleHref);
      // Also update sized variants if present
      document.querySelectorAll('link[rel="apple-touch-icon"][sizes]').forEach((el) => {
        el.setAttribute('href', appleHref);
      });
    }

    // 3) Point the PWA manifest at the backend-served dynamic manifest.
    //    A blob: URL manifest is blocked by CSP (default-src 'self') and is
    //    ignored by the install prompt; the backend endpoint serves a real
    //    same-origin manifest with the admin's pwa_icon/logo/favicon URLs.
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.setAttribute('href', '/api/settings/pwa-manifest/');
    }

    // 4) document.title — mirror the admin's pwa_title so the browser tab
    //    shows the exact configured name (no duplication).
    const title = String(systemSettings.pwa_title || '').trim();
    const fallbackTitle = `${systemSettings.site_name || 'کادولار'} - فروشگاه آنلاین کتاب و تابلو`;
    document.title = title || fallbackTitle;

    // 5) theme-color meta — color of the installed PWA window / status bar.
    const themeColor = String(systemSettings.pwa_window_color || '').trim();
    if (themeColor && /^#[0-9a-fA-F]{6}$/.test(themeColor)) {
      let meta = document.querySelector('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', themeColor);
    }
  }, [systemSettings.favicon_url, systemSettings.logo_url, systemSettings.pwa_icon_url,
      systemSettings.pwa_title, systemSettings.pwa_window_color, systemSettings.site_name]);

  const value = useMemo(
    () => ({ systemSettings, resolveMediaUrl, reloadSystemSettings }),
    [systemSettings, resolveMediaUrl, reloadSystemSettings]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
